import React, { useState } from 'react'
import { Accordion, AccordionDetails, AccordionSummary, TextField, Typography, Box, List, ListItem, ListItemText, Grid, useTheme } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import classNames from 'classnames'
import { useStyles } from './style'
import { removeLeadingZeros } from '../../../../util/form-utils'

const Benchmark = ({ assessment, setAssessment }) => {
  const classes = useStyles()
  const { spacing } = useTheme()
  const [expanded, setExpanded] = useState(assessment.metaSkills[0].id)

  const scoreDictionary = assessment.benchmarkScores.reduce((acc, { metaSkillId, skills }) => {
    skills.forEach(({ skillId, score }) => {
      acc[`${metaSkillId}_${skillId}`] = score
    })
    return acc
  }, {})

  const getScore = (metaSkillId, skillId) => scoreDictionary[`${metaSkillId}_${skillId}`]

  const updateScore = ({ benchmarkScores, metaSkillId, skillId, newScore }) => {
    return benchmarkScores.map(metaSkill => {
      if (metaSkill.metaSkillId === metaSkillId) {
        return {
          ...metaSkill,
          skills: metaSkill.skills.map(skill =>
            skill.skillId === skillId
              ? { ...skill, score: newScore }
              : skill
          )
        }
      }
      return metaSkill
    })
  }

  const handleAccordionExpanded = (metaSkill) => (_event, isExpanded) => {
    setExpanded(isExpanded ? metaSkill : false)
  }

  const handleBenchmarkScoringChange = (metaSkillId, skillId, event) => {
    event.target.value = removeLeadingZeros(event.target.value)
    const newScore = Number(event.target.value)

    const updatedBenchmarkScores = updateScore({
      benchmarkScores: assessment.benchmarkScores,
      metaSkillId,
      skillId,
      newScore
    })

    setAssessment((prevData) => ({
      ...prevData,
      benchmarkScores: updatedBenchmarkScores
    }))
  }

  return (
    <>
      <Box
        display='flex'
        flexDirection='row'
        justifyContent='space-between'
        alignItems='center'
        style={{ paddingTop: spacing(3), marginBottom: spacing(2) }}
      >
        <Box>
          <Grid item xs={12} className={classNames(classes.root)}>
            <Typography className={classes.heading}>
              Benchmark Scoring
            </Typography>
            <Typography variant='subtitle2' className={classes.subtitleMap}>
              This is the default Benchmark Score for the Skills of the
              assessment. When a new tenant be activated for the first time this
              will be the default score for the skills.
            </Typography>
            <Typography variant='body2' color='textSecondary'>
              Please specify the default score for each skill.
            </Typography>
          </Grid>
        </Box>
      </Box>
      <div>
        {assessment.metaSkills.map((metaSkillObj) => (
          <Accordion
            elevation={metaSkillObj.order === 1 ? 0 : 1}
            classes={{
              root: classes.accordion,
              expanded: classes.accordionExpanded
            }}
            key={metaSkillObj.id}
            expanded={expanded === metaSkillObj.id}
            onChange={handleAccordionExpanded(metaSkillObj.id)}
            data-test={`assessment-metaSkill-benchmark-accordion-${metaSkillObj.order - 1}`}
          >
            <AccordionSummary
              classes={{ root: classes.summary }}
              expandIcon={<ExpandMoreIcon />}
            >
              <Typography
                variant='subtitle1'
                data-test={`assessment-metaSkill-benchmark-accordion-title-${metaSkillObj.order - 1}`}
              >
                {metaSkillObj.name}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List>
                {metaSkillObj.skills.map((skillObj) => (
                  <ListItem key={skillObj.id}>
                    <ListItemText
                      disableTypography
                      primary={
                        <Typography
                          variant='body2'
                          className={classes.subtitleMap}
                        >
                          {skillObj.name}
                        </Typography>
                      }
                    />
                    <Typography variant='body' className={classes.scoreLabel}>
                      Score:{' '}
                    </Typography>
                    <TextField
                      className={classes.scoreInput}
                      type='number'
                      value={getScore(metaSkillObj.id, skillObj.id)}
                      onChange={(e) => handleBenchmarkScoringChange(metaSkillObj.id, skillObj.id, e)}
                      inputProps={{ min: 0, max: 100 }}
                      data-test={`assessment-metaSkill-benchmark-accordion-${metaSkillObj.order - 1}-skill-score-${skillObj.order - 1}`}
                    />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        ))}
      </div>
    </>
  )
}

export default Benchmark
